import { useEffect, useRef } from 'react';
import { Loader2, Smartphone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getStatusBadge, getMessageIcon, getMessageStyle } from './utils';
import type { DeviceStreamState } from './shared-types';

export const DeviceCard = ({
  name,
  state,
  onClick,
  isSelected,
}: {
  name: string;
  state: DeviceStreamState;
  onClick?: () => void;
  isSelected?: boolean;
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [state.messages.length]);

  return (
    <div
      className={cn(
        "flex flex-col h-full cursor-pointer rounded-lg border bg-card hover:border-primary/40 transition-colors",
        isSelected && "border-primary",
      )}
      onClick={onClick}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <div className="flex items-center gap-2 min-w-0">
          <Smartphone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="text-sm font-medium truncate">{name}</span>
        </div>
        <div className="flex items-center gap-2.5 shrink-0">
          {state.step > 0 && state.status === 'running' && (
            <span className="text-[11px] text-muted-foreground font-mono tabular-nums">
              {state.step}/{state.maxSteps}
            </span>
          )}
          {getStatusBadge(state.status)}
        </div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-2 space-y-1.5">
        {state.messages.length === 0 && state.status === 'running' && (
          <div className="flex items-center gap-2 text-muted-foreground text-xs p-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            {state.step > 0
              ? `Agent working — awaiting next action (Step ${state.step})`
              : 'Starting agent...'}
          </div>
        )}
        {state.messages.map((msg) => (
          <div
            key={msg.id}
            className={cn(
              'px-2.5 py-1.5 rounded-md border text-xs',
              getMessageStyle(msg.type),
            )}
          >
            <div className="flex items-center gap-1.5 mb-0.5 text-muted-foreground">
              {getMessageIcon(msg.type)}
              <span className="capitalize text-[10px] font-medium">{msg.type}</span>
              <span className="ml-auto text-[10px] font-mono tabular-nums">
                {msg.timestamp.toLocaleTimeString()}
              </span>
            </div>
            <p className="whitespace-pre-wrap break-words leading-relaxed">{msg.content}</p>
          </div>
        ))}
      </div>
    </div>
  );
};
